/**
 * What KIND of license a registry record is, for every decision that asks
 * "does this account hold a state license?" or "a controlled-substance
 * credential?". The ONE definition: the compliance SQL (dashboard cards, the
 * Needs Attention lists, Found License Fills a Gap, the Matching queue's
 * risk-first rank), the order check at checkout and in the cart, and the
 * matchmaker's one-business-license-per-kind rule all read it from here.
 *
 * Decision (Chris, 2026-09-27): "A state license means a state board license
 * or a state drug permit only." So:
 *
 *   state                    — registry source type STATE_BOARD or
 *                              STATE_DRUG_PERMIT, except an animal-euthanasia
 *                              drug registration.
 *   dea                      — source type DEA.
 *   stateControlledSubstance — source type CONTROLLED_SUBSTANCE (a state CSR).
 *                              It counts on the controlled side only, where
 *                              'controlled' means DEA or a state CSR (Chris,
 *                              2026-09-18). It is never the account's state
 *                              license.
 *   animalEuthanasia         — an animal-euthanasia drug registration
 *                              (PD-15726: a shelter's permit to buy sodium
 *                              pentobarbital for euthanasia only). Its registry
 *                              rows are STATE_BOARD, because a state board
 *                              issues it, but it is its own kind: it never
 *                              fills or satisfies the state-license slot and
 *                              counts as neither state nor controlled (the
 *                              default chosen 2026-09-27; it may later move to
 *                              the controlled side). It is still shown on the
 *                              Licenses tab like any other license.
 *   other                    — everything else (NPI, DEA CMEA, DEA chemical,
 *                              tobacco, liquor, unknown).
 *
 * Not the same question as "which source types are state-ISSUED"
 * (`ISSUING_JURISDICTION_SOURCE_TYPES`): a state CSR is state-issued, so it is
 * sourced from the state and its `state_code` names the issuing board, but it
 * is not the account's state license.
 */

/** The canonical category, spelled as `license-category.ts` spells it. */
export const ANIMAL_EUTHANASIA_DRUG_REGISTRATION_CATEGORY =
	'ANIMAL_EUTHANASIA_DRUG_REGISTRATION';

/**
 * Specialty registrations whose kind is carried by the category rather than by
 * `lm_registry.source_type` (PD-15726). DEA CMEA self-certification is the other
 * specialty kind and needs no entry: it has its own source type ('DEA_CMEA').
 */
export const SPECIALTY_LICENSE_CATEGORIES: ReadonlyArray<string> = [
	ANIMAL_EUTHANASIA_DRUG_REGISTRATION_CATEGORY,
];

/** Registry source types a state license can have: a board license or a drug permit. */
export const STATE_LICENSE_SOURCE_TYPES = [
	'STATE_BOARD',
	'STATE_DRUG_PERMIT',
] as const;

/** Registry source types on the controlled side: DEA or a state CSR. */
export const CONTROLLED_LICENSE_SOURCE_TYPES = [
	'DEA',
	'CONTROLLED_SUBSTANCE',
] as const;

export interface AnimalEuthanasiaDrugRegistrationLabelModel {
	/** `lm_registry.state_code`, the issuing jurisdiction. */
	stateCode: string;
	/** `lm_registry.license_type`, exactly as the board publishes it. */
	rawLicenseType: string;
}

/**
 * The registrations that carry the category, by (issuing state, exact board
 * label).
 *
 * SOURCE OF TRUTH. ANIMAL_EUTHANASIA_DRUG_REGISTRATIONS in
 * scripts/license-management/lib/veterinary-scope-policy.mjs, which records
 * each state's statute and whose lock-in keeps the registry category and this
 * list in step in both directions. The deployed apps cannot import that
 * script, so the list is mirrored here; license-kind.test.ts imports the
 * register and fails when the two disagree.
 *
 * WHY THE LABEL AS WELL AS THE CATEGORY. A registry row carries the category,
 * but a `client_license_match` row carries only the registry's state and label
 * (`registry_state_code`, `registry_license_type`), and the matchmaker's
 * one-per-kind rule and the queue's competing groups run over hundreds of
 * thousands of those rows, where a cross-database lookup per row is not
 * affordable. The label is the key the category is assigned by, so the two
 * readings agree.
 */
export const ANIMAL_EUTHANASIA_DRUG_REGISTRATION_LABELS: ReadonlyArray<AnimalEuthanasiaDrugRegistrationLabelModel> =
	Object.freeze(
		[
			{
				stateCode: 'WA',
				rawLicenseType:
					'Drug Animal Control/Humane Society Registration Sodium Pentobarbital',
			},
			{ stateCode: 'OR', rawLicenseType: 'Animal Euthanasia' },
			{ stateCode: 'MI', rawLicenseType: 'CS_ANIMAL' },
		].map((entry) => Object.freeze(entry)),
	);

export type RegistryLicenseKind =
	| 'state'
	| 'dea'
	| 'stateControlledSubstance'
	| 'animalEuthanasia'
	| 'other';

/** The registry facts the kind is decided by. Any may be missing. */
export interface RegistryLicenseKindInputModel {
	/** `lm_registry.source_type` */
	sourceType: string | null | undefined;
	/** `lm_registry.license_category` */
	licenseCategory?: string | null;
	/** `lm_registry.state_code` */
	stateCode?: string | null;
	/** `lm_registry.license_type` */
	licenseType?: string | null;
}

/** True for an animal-euthanasia drug registration, by its category or its (state, label). */
export function isAnimalEuthanasiaDrugRegistration(
	registry: Omit<RegistryLicenseKindInputModel, 'sourceType'>,
): boolean {
	const category = registry.licenseCategory?.trim().toUpperCase() ?? '';
	if (category === ANIMAL_EUTHANASIA_DRUG_REGISTRATION_CATEGORY) {
		return true;
	}
	if (registry.stateCode == null || registry.licenseType == null) {
		return false;
	}
	const state = registry.stateCode.trim().toUpperCase();
	return ANIMAL_EUTHANASIA_DRUG_REGISTRATION_LABELS.some(
		(entry) =>
			entry.stateCode === state &&
			entry.rawLicenseType === registry.licenseType,
	);
}

export function registryLicenseKind(
	registry: RegistryLicenseKindInputModel,
): RegistryLicenseKind {
	if (isAnimalEuthanasiaDrugRegistration(registry)) {
		return 'animalEuthanasia';
	}
	const sourceType = registry.sourceType?.trim().toUpperCase() ?? '';
	if (
		(STATE_LICENSE_SOURCE_TYPES as ReadonlyArray<string>).includes(sourceType)
	) {
		return 'state';
	}
	if (sourceType === 'DEA') {
		return 'dea';
	}
	if (sourceType === 'CONTROLLED_SUBSTANCE') {
		return 'stateControlledSubstance';
	}
	return 'other';
}

/** The account's state license: a state board license or a state drug permit. */
export function isStateLicenseRegistry(
	registry: RegistryLicenseKindInputModel,
): boolean {
	return registryLicenseKind(registry) === 'state';
}

/** A controlled-substance credential: a DEA registration or a state CSR. */
export function isControlledLicenseRegistry(
	registry: RegistryLicenseKindInputModel,
): boolean {
	const kind = registryLicenseKind(registry);
	return kind === 'dea' || kind === 'stateControlledSubstance';
}

/**
 * The key of the matchmaker's one-business-license-per-kind rule (PD-14393 /
 * PD-14440): the registry source type, except that an animal-euthanasia drug
 * registration is keyed by its category. A shelter's confirmed euthanasia
 * permit therefore never takes the state-license slot, and a confirmed state
 * facility license never hides the account's euthanasia permit. Null when the
 * source type is unknown (such a row is never suppressed).
 */
export function clientLevelLicenseKindKey(
	registry: RegistryLicenseKindInputModel,
): string | null {
	if (isAnimalEuthanasiaDrugRegistration(registry)) {
		return ANIMAL_EUTHANASIA_DRUG_REGISTRATION_CATEGORY;
	}
	const sourceType = registry.sourceType?.trim() ?? '';
	return sourceType === '' ? null : sourceType;
}

// ── SQL twins ────────────────────────────────────────────────────────────────

const sqlString = (value: string): string => `'${value.replace(/'/g, "''")}'`;

const sqlList = (values: ReadonlyArray<string>): string =>
	values.map(sqlString).join(', ');

/** Any registered (state, label) pair, over the given column expressions. NULL-safe. */
function euthanasiaLabelSql(
	stateCodeSql: string,
	licenseTypeSql: string,
): string {
	const pairs = ANIMAL_EUTHANASIA_DRUG_REGISTRATION_LABELS.map(
		(entry) =>
			`(COALESCE(${stateCodeSql}, '') = ${sqlString(entry.stateCode)} AND COALESCE(${licenseTypeSql}, '') = ${sqlString(entry.rawLicenseType)})`,
	);
	return `(${pairs.join(' OR ')})`;
}

/**
 * `isAnimalEuthanasiaDrugRegistration` over `workd_mission_control.lm_registry
 * <alias>`. NULL-safe, so it is always TRUE or FALSE and its negation never
 * drops a row through three-valued logic.
 */
export function animalEuthanasiaRegistrySql(alias: string): string {
	return `(COALESCE(${alias}.license_category, '') = ${sqlString(ANIMAL_EUTHANASIA_DRUG_REGISTRATION_CATEGORY)} OR ${euthanasiaLabelSql(`${alias}.state_code`, `${alias}.license_type`)})`;
}

/** `isStateLicenseRegistry` over `lm_registry <alias>`. */
export function stateLicenseRegistrySql(alias: string): string {
	return `(${alias}.source_type IN (${sqlList(STATE_LICENSE_SOURCE_TYPES)}) AND NOT ${animalEuthanasiaRegistrySql(alias)})`;
}

/** `isControlledLicenseRegistry` over `lm_registry <alias>`. */
export function controlledLicenseRegistrySql(alias: string): string {
	return `(${alias}.source_type IN (${sqlList(CONTROLLED_LICENSE_SOURCE_TYPES)}) AND NOT ${animalEuthanasiaRegistrySql(alias)})`;
}

/**
 * `isAnimalEuthanasiaDrugRegistration` over a `client_license_match <alias>`
 * row, which carries the registry's state and label but not its category.
 */
export function animalEuthanasiaMatchSql(alias: string): string {
	return euthanasiaLabelSql(
		`${alias}.registry_state_code`,
		`${alias}.registry_license_type`,
	);
}

/** `isStateLicenseRegistry` over a `client_license_match <alias>` row. */
export function stateLicenseMatchSql(alias: string): string {
	return `(${alias}.registry_source_type IN (${sqlList(STATE_LICENSE_SOURCE_TYPES)}) AND NOT ${animalEuthanasiaMatchSql(alias)})`;
}

/** `isControlledLicenseRegistry` over a `client_license_match <alias>` row. */
export function controlledLicenseMatchSql(alias: string): string {
	return `(${alias}.registry_source_type IN (${sqlList(CONTROLLED_LICENSE_SOURCE_TYPES)}) AND NOT ${animalEuthanasiaMatchSql(alias)})`;
}

/** `clientLevelLicenseKindKey` over a `client_license_match <alias>` row. NULL when the source type is. */
export function clientLevelLicenseKindKeySql(alias: string): string {
	return `(CASE WHEN ${animalEuthanasiaMatchSql(alias)} THEN ${sqlString(ANIMAL_EUTHANASIA_DRUG_REGISTRATION_CATEGORY)} ELSE ${alias}.registry_source_type END)`;
}

/**
 * `clientLevelLicenseKindKey` over a `workd_mission_control.lm_registry <alias>`
 * row (PD-17231). Unlike the match-row twin above, a registry row carries its
 * category, so a euthanasia registration is recognized by category or label.
 * NULL when the source type is.
 */
export function clientLevelLicenseKindKeyRegistrySql(alias: string): string {
	return `(CASE WHEN ${animalEuthanasiaRegistrySql(alias)} THEN ${sqlString(ANIMAL_EUTHANASIA_DRUG_REGISTRATION_CATEGORY)} ELSE NULLIF(TRIM(${alias}.source_type), '') END)`;
}
